// Apple RSS marketing-feed client for chart-rank enrichment (Milestone M3).
//
// See src/config.js for the verified CORS finding and the enable/disable
// decision. This module is the only place that touches the network for charts;
// the rank parsing/matching lives in src/domain/chartRank.js and is pure.
//
// Feeds (per storefront):
//   top-free:     https://rss.applemarketingtools.com/api/v2/<cc>/apps/top-free/<limit>/apps.json
//   top-paid:     https://rss.applemarketingtools.com/api/v2/<cc>/apps/top-paid/<limit>/apps.json
//   top-grossing: https://itunes.apple.com/<cc>/rss/topgrossingapplications/limit=<limit>/json
//
// NOTE: Apple removed top-grossing from the marketing-tools host — every
// rss.applemarketingtools.com/.../apps/top-grossing/... URL now 404s. The
// legacy iTunes RSS feed above still serves top-grossing for every storefront;
// it returns a different JSON shape that domain/chartRank.js normalizes.
//
// When CHART_CONFIG.proxyBase is set, requests are routed through the proxy as
//   <proxyBase>/api/apple-rss?country=<cc>&type=top-free&limit=<limit>
// which returns the raw Apple JSON with permissive CORS headers.

import { CHART_CONFIG } from '../config.js';
import { parseChartFeed, parseChartBrowserFeed, findChartRank, summarizeChart } from '../domain/chartRank.js';

const APPLE_RSS_BASE = 'https://rss.applemarketingtools.com/api/v2';
// Legacy iTunes RSS host, used only for the chart types Apple no longer serves
// from the marketing-tools host. Maps a chart type to its legacy feed slug.
const ITUNES_RSS_BASE = 'https://itunes.apple.com';
const ITUNES_RSS_FEED_NAME = Object.freeze({
  'top-grossing': 'topgrossingapplications',
});

// In-memory cache for the current page session, keyed by country|type|limit.
// Each value is a Promise so concurrent callers share a single in-flight fetch.
const feedCache = new Map();

/**
 * Clear the session feed cache. Exposed mainly for tests.
 */
export function clearChartCache() {
  feedCache.clear();
}

/**
 * Build the request URL for a feed, routing through the proxy when configured.
 * @param {string} country
 * @param {'top-free'|'top-paid'|'top-grossing'} type
 * @param {number} limit
 * @param {string|null} [genreId] - Optional Apple genre ID to filter by category.
 * @returns {string}
 */
export function buildFeedUrl(country, type, limit, genreId = null) {
  const base = CHART_CONFIG.proxyBase;
  if (base) {
    const url = new URL('/api/apple-rss', base);
    url.searchParams.set('country', country);
    url.searchParams.set('type', type);
    url.searchParams.set('limit', String(limit));
    if (genreId) url.searchParams.set('genreId', genreId);
    return url.toString();
  }
  // top-grossing is no longer on the marketing-tools host — use the legacy
  // iTunes RSS feed. It has no genre-filtered variant, so when a genreId is
  // requested we fall through to the marketing-tools URL below (which 404s for
  // grossing and is handled by the caller's per-feed fallback).
  const itunesFeed = ITUNES_RSS_FEED_NAME[type];
  if (itunesFeed && !genreId) {
    return `${ITUNES_RSS_BASE}/${encodeURIComponent(country)}/rss/${itunesFeed}/limit=${limit}/json`;
  }
  // Apple RSS URL with optional genre filter:
  //   with genre:    .../api/v2/{cc}/apps/{type}/{limit}/{genreId}/apps.json
  //   without genre: .../api/v2/{cc}/apps/{type}/{limit}/apps.json
  const genrePath = genreId ? `/${encodeURIComponent(genreId)}` : '';
  return `${APPLE_RSS_BASE}/${encodeURIComponent(country)}/apps/${type}/${limit}${genrePath}/apps.json`;
}

/**
 * Fetch and parse a genre-filtered chart feed (top-free or top-paid for a
 * specific category). Results are cached for the session. Resolves to at most
 * `topN` ChartEntry items, or rejects on network/parse failure.
 *
 * @param {string} country
 * @param {'top-free'|'top-paid'} type
 * @param {string} genreId - Apple genre ID, e.g. '6014' for Games.
 * @param {number} [topN=5] - Number of top entries to return.
 * @returns {Promise<import('../domain/chartRank.js').ChartEntry[]>}
 */
export function fetchCategoryChart(country, type, genreId, topN = 5) {
  const key = `cat|${country}|${type}|${genreId}|${topN}`;
  const cached = feedCache.get(key);
  if (cached) return cached;

  const url = buildFeedUrl(country, type, topN, genreId);
  const promise = (async () => {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Category chart ${type}/${genreId} (${country}) failed: HTTP ${res.status}`);
    }
    const data = await res.json();
    return parseChartFeed(data).slice(0, topN);
  })();

  promise.catch(() => feedCache.delete(key));
  feedCache.set(key, promise);
  return promise;
}

/**
 * Fetch and parse one chart feed, cached for the session. Resolves to a list of
 * ChartEntry on success, or rejects if the request/parse fails.
 * @param {string} country
 * @param {'top-free'|'top-paid'|'top-grossing'} type
 * @param {number} [limit]
 * @returns {Promise<import('../domain/chartRank.js').ChartEntry[]>}
 */
export function fetchChart(country, type, limit = CHART_CONFIG.limit) {
  const key = `${country}|${type}|${limit}`;
  const cached = feedCache.get(key);
  if (cached) return cached;

  const url = buildFeedUrl(country, type, limit);
  const promise = (async () => {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`RSS feed ${type} (${country}) failed: HTTP ${res.status}`);
    }
    const data = await res.json();
    return parseChartFeed(data);
  })();

  // Cache the promise; on failure, evict so a later attempt can retry.
  promise.catch(() => feedCache.delete(key));
  feedCache.set(key, promise);
  return promise;
}

/**
 * Resolve the chart standing for one app id in one storefront by reading the
 * top-free, top-paid, and top-grossing feeds. Never rejects: feed failures are
 * folded into the returned RowChart so chart errors stay isolated.
 *
 * @param {number|string} trackId
 * @param {string} country
 * @param {number} [limit]
 * @returns {Promise<import('../domain/chartRank.js').RowChart>}
 */
export async function chartStandingFor(trackId, country, limit = CHART_CONFIG.limit) {
  const [free, paid, grossing] = await Promise.allSettled([
    fetchChart(country, 'top-free', limit),
    fetchChart(country, 'top-paid', limit),
    fetchChart(country, 'top-grossing', limit),
  ]);

  const freeOk = free.status === 'fulfilled';
  const paidOk = paid.status === 'fulfilled';
  const grossingOk = grossing.status === 'fulfilled';

  return summarizeChart({
    freeRank: freeOk ? findChartRank(trackId, free.value) : null,
    paidRank: paidOk ? findChartRank(trackId, paid.value) : null,
    grossingRank: grossingOk ? findChartRank(trackId, grossing.value) : null,
    freeOk,
    paidOk,
    grossingOk,
    limit,
  });
}

/**
 * Fetch a chart feed returning full ChartBrowserEntry data (rank, trackId, name,
 * artistName, artworkUrl, primaryGenre, genreId). Uses a separate cache key
 * so it does not collide with the minimal fetchChart() cache.
 *
 * NOTE: Subject to the same CORS restriction as fetchChart() — only works when
 * CHART_CONFIG.proxyBase is set (see src/config.js).
 *
 * @param {string} country
 * @param {'top-free'|'top-paid'|'top-grossing'} type
 * @param {number} [limit]
 * @returns {Promise<import('../domain/chartRank.js').ChartBrowserEntry[]>}
 */
export function fetchChartFull(country, type, limit = 20) {
  const key = `full|${country}|${type}|${limit}`;
  const cached = feedCache.get(key);
  if (cached) return cached;

  const url = buildFeedUrl(country, type, limit);
  const promise = (async () => {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`RSS feed ${type} (${country}) failed: HTTP ${res.status}`);
    }
    const data = await res.json();
    return parseChartBrowserFeed(data);
  })();

  promise.catch(() => feedCache.delete(key));
  feedCache.set(key, promise);
  return promise;
}

/**
 * Run an async mapper over items with a bounded number of concurrent tasks so
 * chart enrichment does not fire a burst of feed requests at once. Results are
 * returned in the original item order.
 * @template T, R
 * @param {T[]} items
 * @param {number} limit - max concurrent tasks (>= 1).
 * @param {(item: T, index: number) => Promise<R>} mapper
 * @returns {Promise<R[]>}
 */
export async function mapWithConcurrency(items, limit, mapper) {
  const list = Array.isArray(items) ? items : [];
  const max = Math.max(1, Number.isFinite(limit) ? Math.floor(limit) : 1);
  const results = new Array(list.length);
  let cursor = 0;

  async function worker() {
    while (cursor < list.length) {
      const index = cursor++;
      results[index] = await mapper(list[index], index);
    }
  }

  const workers = [];
  for (let i = 0; i < Math.min(max, list.length); i++) {
    workers.push(worker());
  }
  await Promise.all(workers);
  return results;
}
